import * as React from 'react'

import { observer } from 'mobx-react'

import { Loader } from '~/client/src/shared/components/Loader'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { IUserListSelectorStores } from '~/client/src/shared/components/UserListSelector/useUserListSelectorData'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { ChatShellStore } from '../web/ChatShell.store'
import ChatLogScreen from './ChatLogScreen'
import DraftChatScreen from './DraftChatScreen'
import GroupFormScreen, { GroupFormMode } from './GroupFormScreen'
import InboxScreen from './InboxScreen'
import NewChatScreen from './NewChatScreen'
import WorkflowCardDataStore from './workflow/WorkflowCardData.store'

interface IProps {
  shell: ChatShellStore
  projectDateStore: ProjectDateStore
  selectorStores: IUserListSelectorStores
  createWorkflowCardStore: () => WorkflowCardDataStore
  shouldSendOnEnter?: boolean
  onClose?: () => void
}

function ChatScreens({
  shell,
  projectDateStore,
  selectorStores,
  createWorkflowCardStore,
  shouldSendOnEnter,
  onClose,
}: IProps): React.ReactElement {
  const { session, directory, screen } = shell

  if (!session) {
    return <Loader hint={Localization.translator.loading} />
  }

  const renderConnectionBanner = (): React.ReactElement => {
    if (session.connectionStatus !== 'disconnected') {
      return null
    }

    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:bg-muted tw:py-1">
        <Spinner size="xs" />
        <span className="tw:text-xs tw:text-muted-foreground">
          {Localization.translator.reconnecting}
        </span>
      </div>
    )
  }

  const renderScreen = (): React.ReactElement => {
    switch (screen.kind) {
      case 'conversation':
        return (
          <ChatLogScreen
            key={screen.chat.id}
            session={session}
            directory={directory}
            chat={screen.chat}
            projectDateStore={projectDateStore}
            uploadImage={shell.uploadImage}
            createWorkflowCardStore={createWorkflowCardStore}
            shouldSendOnEnter={shouldSendOnEnter}
            onMentionPress={shell.openUserCard}
            onOpenWorkflow={shell.openWorkflow}
            onOpenGroupDetails={chat =>
              shell.setScreen({ kind: 'groupForm', chat })
            }
            onBack={shell.backToInbox}
          />
        )
      case 'newChat':
        return (
          <NewChatScreen
            session={session}
            directory={directory}
            onOpenChat={shell.openConversation}
            onOpenDraft={recipient =>
              shell.setScreen({ kind: 'draft', recipient })
            }
            onNewGroup={() => shell.setScreen({ kind: 'groupForm' })}
            onBack={shell.backToInbox}
          />
        )
      case 'draft':
        return (
          <DraftChatScreen
            key={screen.recipient.id}
            session={session}
            directory={directory}
            recipient={screen.recipient}
            projectDateStore={projectDateStore}
            uploadImage={shell.uploadImage}
            shouldSendOnEnter={shouldSendOnEnter}
            onMaterialized={shell.openConversation}
            onMentionPress={shell.openUserCard}
            onBack={shell.backToInbox}
          />
        )
      case 'groupForm': {
        // Without a chat the form creates; with one it edits when the current
        // user created the group and is read-only otherwise.
        const mode: GroupFormMode = !screen.chat
          ? { kind: 'create' }
          : screen.chat.createdBy === session.config.userId
            ? { kind: 'edit', chat: screen.chat }
            : { kind: 'view', chat: screen.chat }
        return (
          <GroupFormScreen
            session={session}
            directory={directory}
            mode={mode}
            selectorStores={selectorStores}
            onCreated={shell.openConversation}
            onBack={
              screen.chat
                ? () => shell.openConversation(screen.chat)
                : () => shell.setScreen({ kind: 'newChat' })
            }
          />
        )
      }
      default:
        return (
          <InboxScreen
            session={session}
            directory={directory}
            projectDateStore={projectDateStore}
            onOpenChat={shell.openConversation}
            onNewChat={() => shell.setScreen({ kind: 'newChat' })}
            onClose={onClose}
          />
        )
    }
  }

  return (
    <div className="tw:flex tw:h-full tw:flex-col">
      {renderConnectionBanner()}
      <div className="tw:min-h-0 tw:flex-1">{renderScreen()}</div>
    </div>
  )
}

export default observer(ChatScreens)
